import { TemplateType } from '@frontend/app/types/globalTypes';
import React, { useMemo, useState } from 'react';
import { useSaveProgramMutation } from '@frontend/app/containers/Communities/AddOrEditCommunity/hooks/useSaveProgramMutation';
import { useCreateMessageTemplateMutation, useDeleteMessageTemplateMutation, useGetMessageTemplates, useGetProgramById } from '@frontend/app/hooks';
import { omit } from 'lodash';
import { OFFER_SOURCE, OFFER_TYPE, SEND_STA_TASK_ID } from '@frontend/applications/AffiliatesApp/types/globalTypes';
import { MessageTemplatesQuery_templates } from '@frontend/app/queries/types/MessageTemplatesQuery';
import { P } from '@frontend/shadcn/components/typography/p';
import { Button } from '@frontend/shadcn/components/ui/button';
import {
  PlusIcon,
  EllipsisIcon,
} from '@revfluence/fresh-icons/regular/esm';
import { Skeleton } from '@frontend/shadcn/components/ui/skeleton';
import { useApolloClient, useGetOffersBySearchQuery } from '@frontend/applications/AffiliatesApp/hooks';
import { Card, CardContent } from '@frontend/shadcn/components/ui/card';
import { Badge } from '@frontend/shadcn/components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@frontend/shadcn/components/ui/accordion';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@frontend/shadcn/components/ui/dropdown-menu';
import { cn } from '@frontend/shadcn/lib/utils';
import { RefreshOfferDefaultImage } from '@frontend/applications/AffiliatesApp/components/OfferImage/RefreshOfferDefaultImage';
import { GetOffersBySearchQuery_offers } from '@frontend/applications/AffiliatesApp/queries/types/GetOffersBySearchQuery';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useMessagingContext } from '@frontend/hooks';
import { Modal } from '@revfluence/fresh';
import { logger } from '@common';
import { useGetOfferEmailBody } from './useGetOfferEmailBody';
import { SectionCard } from '../../CampaignPage/components/SectionCard';
import { EmailTemplateDrawer } from '../components/EmailTemplateDrawer';

type TProps = {
  projectId: number;
};

export const OfferEmailTemplates = (props: TProps) => {
  const { projectId } = props;

  const { data: program } = useGetProgramById({
    variables: {
      id: projectId,
    },
  });

  const staApolloClient = useApolloClient();

  const { data: offers } = useGetOffersBySearchQuery({
    variables: {
      query: {
        programId: projectId,
      },
    },
    client: staApolloClient,
  });

  const promoOffer = offers?.offers?.find((offer) => offer.type === OFFER_TYPE.PROMO_CODE);
  const salesLinkOffer = offers?.offers?.find((offer) => offer.type === OFFER_TYPE.LINK);

  const defaultTemplates = program?.program?.programMetadata?.defaultOffersEmailTemplate || {};

  const [saveProgram] = useSaveProgramMutation();

  const [deleteTemplate] = useDeleteMessageTemplateMutation();
  const [createTemplate] = useCreateMessageTemplateMutation();

  const { showMessage, showError } = useMessagingContext();

  // Get default content for Promo Code templates
  const {
    subject: defaultPromoSubject,
    blocks: defaultPromoBlocks,
    entityMap: defaultPromoEntityMap,
  } = useGetOfferEmailBody({
    programId: projectId,
    taskId: SEND_STA_TASK_ID.send_promo_task,
  });

  // Get default content for Sales Link templates
  const {
    subject: defaultLinkSubject,
    blocks: defaultLinkBlocks,
    entityMap: defaultLinkEntityMap,
  } = useGetOfferEmailBody({
    programId: projectId,
    taskId: SEND_STA_TASK_ID.send_link_task,
  });

  const handleMakeDefault = async (offerId: number, templateId: number) => {
    if (defaultTemplates[offerId] === templateId || !program?.program || !offerId) return;
    await saveProgram({
      variables: {
        program: {
          id: projectId,
          programMetadata: {
            ...omit(program.program.programMetadata || {}, '__typename'),
            startDate: program.program.programMetadata?.startDate || new Date().toISOString(),
            defaultOffersEmailTemplate: {
              ...defaultTemplates,
              [offerId]: templateId,
            },
          },
        },
      },
    });
  };

  const handleDuplicate = async (template: MessageTemplatesQuery_templates) => {
    if (!template) return;

    const duplicateData = {
      title: `${template.title ?? 'Template'} (Copy)`,
      subject: template.subject ?? '',
      text: template.text ?? '',
      type: template.type,
      programId: template.programId ?? projectId,
    };

    try {
      const { data: duplicatedTemplate } = await createTemplate({
        variables: { data: duplicateData },
      });
      if (duplicatedTemplate?.template) {
        showMessage({ type: 'success', content: 'Template duplicated successfully' });
      }
    } catch (error) {
      showError(error);
    }
  };

  const handleDelete = (template: MessageTemplatesQuery_templates) => {
    Modal.confirm({
      title: 'Are you sure you want to delete this template?',
      icon: <ExclamationCircleOutlined />,
      content: "You can't undo this action",
      okText: 'Delete',
      okButtonProps: { danger: true },
      centered: true,
      zIndex: 99999,
      onOk() {
        deleteTemplate({
          variables: { id: template.id },
        }).then(({ data: { success } = {} }) => {
          if (success) {
            showMessage({ type: 'success', content: 'Template has been deleted' });
          }
        }).catch(logger.error);
      },
    });
  };

  const { templates, loading: messageTemplatesLoading, refetch } = useGetMessageTemplates();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<MessageTemplatesQuery_templates | null>(null);
  const [currentOfferType, setCurrentOfferType] = useState<TemplateType | null>(null);

  const promoEmailTemplates = useMemo(() => {
    if (messageTemplatesLoading) return [];
    return templates.filter(
      (template) => template.type === TemplateType.PROMO_OFFER_TEMPLATE && template.programId === projectId,
    );
  }, [messageTemplatesLoading, templates, projectId]);

  const salesLinkEmailTemplates = useMemo(() => {
    if (messageTemplatesLoading) return [];
    return templates.filter(
      (template) => template.type === TemplateType.SALES_LINK_OFFER_TEMPLATE && template.programId === projectId,
    );
  }, [messageTemplatesLoading, templates, projectId]);

  const loading = messageTemplatesLoading;

  const handleEditClick = (template: MessageTemplatesQuery_templates) => {
    setSelectedTemplate(template);
    setCurrentOfferType(template.type); // Store type for drawer if needed
    setIsDrawerOpen(true);
  };

  const handleCreateClick = (offerType: TemplateType) => {
    setSelectedTemplate(null);
    setCurrentOfferType(offerType); // Store type for drawer
    setIsDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
    setSelectedTemplate(null);
    setCurrentOfferType(null);
  };

  const handleMutationSuccess = () => {
    refetch();
    handleDrawerClose();
  };

  const activeOfferIdForDrawer = useMemo(() => {
    return currentOfferType === TemplateType.PROMO_OFFER_TEMPLATE ? promoOffer?.id : salesLinkOffer?.id;
  }, [currentOfferType, promoOffer, salesLinkOffer]);

  const renderTemplateItem = (template: MessageTemplatesQuery_templates, offerId: number | undefined) => (
    <Card key={template.id} className="rounded-xl border border-border bg-card">
      <CardContent className="px-4 py-1.5 flex items-center">
        <P className="mb-0" fontWeight="font-medium">
          {template.title}
        </P>
        {offerId && defaultTemplates[offerId] === template.id && (
          <Badge
            variant="secondary"
            className="bg-grey-1 text-grey-6 text-xs font-normal px-2.5 py-1 rounded-lg ml-2"
          >
            Default
          </Badge>
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="text-muted-foreground hover:bg-muted h-6 w-6 ml-auto"
              data-testid={`more-options-${template.id}`}
            >
              <EllipsisIcon className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="rounded-2xl">
            <DropdownMenuItem onSelect={() => handleEditClick(template)} className="text-sm">
              Edit
            </DropdownMenuItem>
            {offerId && defaultTemplates[offerId] !== template.id && (
            <DropdownMenuItem
              onSelect={() => handleMakeDefault(offerId, template.id)}
              className="text-sm px-2 py-1.5 cursor-pointer"
            >
              Make Default
            </DropdownMenuItem>
              )}
            <DropdownMenuItem
              onSelect={() => handleDuplicate(template)}
              className="text-sm px-2 py-1.5 cursor-pointer"
            >
              Duplicate
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={() => handleDelete(template)}
              className="text-destructive text-sm focus:text-destructive focus:bg-destructive/10 px-2 py-1.5 cursor-pointer"
            >
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </CardContent>
    </Card>
  );

  const renderOfferSection = (
    offerType: TemplateType,
    offer: GetOffersBySearchQuery_offers | undefined,
    templates: MessageTemplatesQuery_templates[],
  ) => {
    if (!offer) return null;
    const templateCount = templates.length;
    const countText = templateCount === 1 ? '1 template' : `${templateCount} templates`;
    const source = offer.type === OFFER_TYPE.PROMO_CODE ? OFFER_SOURCE.SHOPIFY : OFFER_SOURCE.TUNE;

    return (
      <AccordionItem value={offer.name} className="border-none mb-3 bg-grey-2 rounded-xl">
        <AccordionTrigger
          className={cn(
            'bg-card border border-border px-3 py-2 w-full flex items-center justify-between rounded-xl',
            'hover:no-underline',
            'data-[state=open]:border-b-0'
          )}
        >
          <div className="flex items-center gap-2">
            <RefreshOfferDefaultImage
              imageUrl={offer.imageUrl}
              source={source}
              icon={offer.icon}
              iconClassName="h-4 w-4"
              className="w-6 h-6 !rounded-lg"
            />
            <P fontWeight="font-medium" className="mb-0">
              {offer.name}
            </P>
          </div>
          <P className="text-muted-foreground mb-0 text-xs border-b border-muted-foreground border-solid ml-auto mr-12">
            {countText}
          </P>
          <div className="flex items-center gap-2 mr-2">
            <Button
              variant="outline"
              size="xs"
              className="font-semibold"
              onClick={(e) => {
                e.stopPropagation();
                handleCreateClick(offerType);
              }}
            >
              <PlusIcon className="mr-1.5 h-3 w-3" />
              Template
            </Button>
          </div>
        </AccordionTrigger>
        <AccordionContent className="border-x border-b border-border rounded-b-xl p-2">
          {loading && (
            <>
              <Skeleton data-testid={`skeleton-${offer.id}-1`} className="h-10 w-full mb-1" />
              <Skeleton data-testid={`skeleton-${offer.id}-2`} className="h-10 w-full mb-1" />
            </>
          )}
          {!loading && templates.length === 0 && (
            <P className="text-center text-muted-foreground text-sm py-4">No templates created yet.</P>
          )}
          <div className="flex flex-col gap-1">
            {!loading && templates.map((template) => renderTemplateItem(template, offer.id))}
          </div>
        </AccordionContent>
      </AccordionItem>
    );
  };

  return (
    <>
      <SectionCard
        title="Offer Email Template"
        description="Set and customize your default email template for Sales Links and Promo Codes."
      >
        <div className="flex flex-col gap-0 min-w-[700px]">
          {' '}
          <Accordion
            type="multiple"
            className="w-full [&_h3]:mb-0"
            defaultValue={[promoOffer?.name, salesLinkOffer?.name].filter(Boolean) as string[]}
          >
            {renderOfferSection(
              TemplateType.PROMO_OFFER_TEMPLATE,
              promoOffer,
              promoEmailTemplates,
            )}
            {renderOfferSection(
              TemplateType.SALES_LINK_OFFER_TEMPLATE,
              salesLinkOffer,
              salesLinkEmailTemplates,
            )}
          </Accordion>
          {loading && !promoOffer && !salesLinkOffer && (
            <>
              <Skeleton data-testid="skeleton-promo" className="h-20 w-full mb-3 rounded-lg" />
              <Skeleton data-testid="skeleton-link" className="h-20 w-full mb-3 rounded-lg" />
            </>
          )}
          {!loading && !promoOffer && !salesLinkOffer && (
            <P className="text-center text-muted-foreground text-sm py-4">No offers found for this program.</P>
          )}
        </div>
      </SectionCard>

      <EmailTemplateDrawer
        open={isDrawerOpen}
        onOpenChange={setIsDrawerOpen}
        template={selectedTemplate}
        projectId={projectId}
        onSaveSuccess={handleMutationSuccess}
        onCancel={handleDrawerClose}
        onDeleteSuccess={handleMutationSuccess}
        defaultTemplateId={activeOfferIdForDrawer !== undefined ? defaultTemplates[activeOfferIdForDrawer] : undefined}
        defaultEmailTitle={selectedTemplate?.title || ''}
        defaultEmailSubject={
          selectedTemplate?.subject ||
          (currentOfferType === TemplateType.PROMO_OFFER_TEMPLATE ? defaultPromoSubject : defaultLinkSubject) ||
          ''
        }
        defaultEmailText={
          selectedTemplate?.text ||
          (currentOfferType === TemplateType.PROMO_OFFER_TEMPLATE
            ? JSON.stringify({
                blocks: defaultPromoBlocks,
                entityMap: defaultPromoEntityMap,
              })
            : JSON.stringify({
                blocks: defaultLinkBlocks,
                entityMap: defaultLinkEntityMap,
              })) ||
          ''
        }
        templateType={currentOfferType}
        handleMakeDefault={(templateId: number) => {
          if (activeOfferIdForDrawer !== undefined) {
            return handleMakeDefault(activeOfferIdForDrawer, templateId);
          }
          return Promise.resolve();
        }}
      />
    </>
  );
};
